import React, { FC, useState } from 'react'
import { Box, Button, CircularProgress, Tooltip } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

import { SoapImprovementSuggestion } from '@/apps/legacy/features/formatter/types/soapImprovement'
import { colorTokens } from '@/apps/legacy/shared/styles/colorTokens'
import { BodyText } from '@/components/atoms/BodyText'

interface SoapImprovementCardProps {
  suggestion: SoapImprovementSuggestion
  onExecute?: (suggestion: SoapImprovementSuggestion) => Promise<void>
  onDismiss?: (suggestionId: string) => Promise<void>
}

const STATUS_TAG: Record<SoapImprovementSuggestion['status'], string | null> = {
  pending: null,
  applied: '実行済',
  dismissed: '却下済',
}

// Notion 流 1-line リスト:
//   [label]  [(i) hover で reason tooltip]  [実行] [却下]
// reason (= suggestion.explanation) は i アイコンの hover tooltip で出す (= 冗長を避ける).
// 実行/却下 button は常時表示 (= hover 隠しだと存在が直感的でない).
// 連続行 + hover bg のみ. dismissed/applied は opacity ダウン + status tag を inline 表示.
export const SoapImprovementCard: FC<SoapImprovementCardProps> = ({
  suggestion,
  onExecute,
  onDismiss,
}) => {
  const [isExecuting, setIsExecuting] = useState(false)
  const [isDismissing, setIsDismissing] = useState(false)
  const isDismissed = suggestion.status === 'dismissed'
  const isPending = suggestion.status === 'pending'
  const isProcessing = isExecuting || isDismissing
  const tag = STATUS_TAG[suggestion.status]

  const handleExecute = async () => {
    if (isProcessing) return
    setIsExecuting(true)
    try {
      await onExecute?.(suggestion)
    } finally {
      setIsExecuting(false)
    }
  }

  const handleDismiss = async () => {
    if (isProcessing) return
    setIsDismissing(true)
    try {
      await onDismiss?.(suggestion.id)
    } finally {
      setIsDismissing(false)
    }
  }

  // 3-column grid layout (= 縦ガタツキ防止):
  //   col1 (1fr): label
  //   col2 (20px): i icon (= 全行で同 x 座標固定)
  //   col3 (88px): tag or buttons (= right-align で右端揃え)
  // 色味は追加せず、 余白とフォント重みだけで分離 (= SOAP bullet と同 spec).
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 20px 88px',
        alignItems: 'center',
        gap: 1,
        py: 0.25,
        opacity: isDismissed ? 0.5 : 1,
      }}
    >
      <BodyText sx={{ minWidth: 0, whiteSpace: 'pre-wrap' }}>
        ・{suggestion.label}
      </BodyText>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {suggestion.explanation && (
          <Tooltip
            title={suggestion.explanation}
            placement="top"
            arrow
            slotProps={{
              tooltip: {
                sx: {
                  fontSize: 12,
                  fontWeight: 500,
                  bgcolor: '#191919',
                  color: '#D4D4D4',
                  px: 1.25,
                  py: 0.75,
                  maxWidth: 320,
                  lineHeight: 1.5,
                },
              },
              arrow: { sx: { color: '#191919' } },
            }}
          >
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: colorTokens.text.secondary,
                cursor: 'help',
                '&:hover': { color: colorTokens.text.primary },
              }}
            >
              <InfoOutlinedIcon sx={{ fontSize: 14 }} />
            </Box>
          </Tooltip>
        )}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 0.25,
        }}
      >
        {tag && (
          <Box
            component="span"
            sx={{
              px: 0.75,
              fontSize: 11,
              fontWeight: 500,
              color: colorTokens.text.secondary,
              border: `1px solid ${colorTokens.border.light}`,
              borderRadius: '4px',
            }}
          >
            {tag}
          </Box>
        )}
        {isPending && (
          <>
            <Button
              size="small"
              variant="text"
              disableElevation
              onClick={handleExecute}
              disabled={isProcessing}
              sx={{
                minWidth: 0,
                px: 1,
                py: 0.25,
                fontSize: 13,
                fontWeight: 500,
                color: colorTokens.text.primary,
                textTransform: 'none',
                borderRadius: 0.75,
                '&:hover': { bgcolor: colorTokens.grey[200] },
              }}
            >
              {isExecuting ? (
                <CircularProgress
                  size={12}
                  thickness={3}
                  sx={{ color: colorTokens.text.secondary }}
                />
              ) : (
                '実行'
              )}
            </Button>
            <Button
              size="small"
              variant="text"
              disableElevation
              onClick={handleDismiss}
              disabled={isProcessing}
              sx={{
                minWidth: 0,
                px: 1,
                py: 0.25,
                fontSize: 13,
                fontWeight: 500,
                color: colorTokens.text.secondary,
                textTransform: 'none',
                borderRadius: 0.75,
                '&:hover': { bgcolor: colorTokens.grey[200] },
              }}
            >
              {isDismissing ? (
                <CircularProgress
                  size={12}
                  thickness={3}
                  sx={{ color: colorTokens.text.secondary }}
                />
              ) : (
                '却下'
              )}
            </Button>
          </>
        )}
      </Box>
    </Box>
  )
}
